/**
 * Volume of 3D inversion models (EarthImager 3D grids): cell volumes, the share inside a
 * resistivity range, and the display strings. Pure, no three.js.
 */

const num = Number.isFinite;
const hasExt = (c) => !!c && [c.x0, c.x1, c.y0, c.y1, c.top, c.bottom].every(num);

/** Median of the positive gaps between the sorted unique values (rounded to 1 mm); null with fewer than 2 values. */
function medianGap(values) {
  const u = [...new Set(values.filter(num).map((v) => Math.round(v * 1000) / 1000))].sort((a, b) => a - b);
  if (u.length < 2) return null;
  const gaps = [];
  for (let i = 1; i < u.length; i++) gaps.push(u[i] - u[i - 1]);
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)] || null;
}

/** { dx, dy, dz } grid spacing from the cells' centres (x, y, depth), or null when an axis has a single value. */
export function gridSpacing(cells) {
  const dx = medianGap(cells.map((c) => c.x));
  const dy = medianGap(cells.map((c) => c.y));
  const dz = medianGap(cells.map((c) => c.depth));
  return dx && dy && dz ? { dx, dy, dz } : null;
}

/** Volume of one cell in m³ from its extents (x0,x1,y0,y1,top,bottom); else from `spacing`; else null. */
export function cellVolume(c, spacing = null) {
  if (hasExt(c)) return Math.abs(c.x1 - c.x0) * Math.abs(c.y1 - c.y0) * Math.abs(c.bottom - c.top);
  return spacing ? spacing.dx * spacing.dy * spacing.dz : null;
}

/**
 * Totals over a population of cells. `inRange(cell, index)` says which are inside the visible range
 * (default: all); `alpha(cell, index)` is the cell's opacity (default: 1 in range, 0 outside) and gives
 * the opaque (alpha 1) and clear (alpha 0) volumes. Cells without a volume are skipped (and not counted).
 */
export function volumeStats(cells, { inRange = null, alpha = null } = {}) {
  const spacing = cells.some((c) => !hasExt(c)) ? gridSpacing(cells) : null;
  let total = 0, inside = 0, nCells = 0, nInside = 0, opaque = 0, translucent = 0, clear = 0, hidden = 0;
  cells.forEach((c, i) => {
    const v = cellVolume(c, spacing);
    if (v === null) return;
    nCells++;
    total += v;
    const inR = !inRange || inRange(c, i);
    if (inR) { inside += v; nInside++; }
    const a = alpha ? alpha(c, i) : (inR ? 1 : 0);
    if (a >= 1) opaque += v;
    else if (a > 0) translucent += v;
    else if (inR) clear += v; // alpha 0 from the opacity curve inside the range
    else hidden += v; // outside the range at opacity 0
  });
  return { total, inside, outside: total - inside, opaque, translucent, clear, hidden, nCells, nInside, units: 'm³' };
}

/** "2,450 m³"; from 10,000 on "12.3 k m³"; null gives an em dash. */
export function formatVolume(v) {
  if (!num(v)) return '—';
  if (v >= 10000) return `${(v / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })} k m³`;
  return `${v < 100 ? v.toFixed(1) : Math.round(v).toLocaleString('en-US')} m³`;
}

/** "Volume: total 2,450 m³ · opaque 980 m³ (40 %) · translucent 270 m³ · clear 1,200 m³ · hidden 500 m³"; translucent and hidden only when not zero. */
export function volumeSummary(stats) {
  if (!stats || stats.nCells === 0) return 'Volume: —';
  const pct = stats.total > 0 ? Math.round((stats.opaque / stats.total) * 100) : 0;
  const part = (label, v) => (v > 1e-9 ? ` · ${label} ${formatVolume(v)}` : '');
  return `Volume: total ${formatVolume(stats.total)} · opaque ${formatVolume(stats.opaque)} (${pct} %)${part('translucent', stats.translucent)} · clear ${formatVolume(stats.clear)}${part('hidden', stats.hidden)}`;
}

/** CSV of x, y, z, resistivity (plus lon, lat when the cells are georeferenced). */
export function cellsToXyzCsv(cells) {
  const geo = cells.some((c) => num(c.lon) && num(c.lat));
  const rows = [geo ? 'x,y,z,resistivity,lon,lat' : 'x,y,z,resistivity'];
  const f = (v) => (num(v) ? String(Math.round(v * 1e6) / 1e6) : '');
  cells.forEach((c) => {
    const z = num(c.z) ? c.z : (num(c.depth) ? -c.depth : null);
    const row = [f(c.x), f(c.y), f(z), f(c.resistivity)];
    if (geo) row.push(f(c.lon), f(c.lat));
    rows.push(row.join(','));
  });
  return `${rows.join('\n')}\n`;
}
